<template>
  <!--
    More tab — bundles the lower-frequency features so the bottom bar
    stays clean. Each card is collapsible so the page is scannable.
  -->
  <div class="px-3 pt-3">

    <!-- Wi-Fi -->
    <details open class="card border-0 shadow-sm rounded-3 mb-3">
      <summary class="card-body p-3 d-flex align-items-center" style="cursor: pointer;">
        <div class="bg-info bg-opacity-10 text-info rounded-circle d-flex align-items-center justify-content-center me-3"
             style="width: 40px; height: 40px;">
          <i class="fa-solid fa-wifi" />
        </div>
        <div class="flex-grow-1">
          <div class="fw-bold">{{ $t('Wi-Fi') }}</div>
          <div class="text-muted small">{{ $t('Free for guests') }}</div>
        </div>
        <i class="fa-solid fa-chevron-down text-muted" />
      </summary>
      <div class="px-3 pb-3">
        <div class="bg-light rounded-3 p-3">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div>
              <div class="small text-muted">{{ $t('Network') }}</div>
              <div class="fw-bold">{{ wifi.ssid }}</div>
            </div>
            <button class="btn btn-sm btn-outline-secondary" @click="copy(wifi.ssid, $t('Network'))">
              <i class="fa-solid fa-copy" />
            </button>
          </div>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <div class="small text-muted">{{ $t('Password') }}</div>
              <div class="fw-bold font-monospace">{{ showPwd ? wifi.password : '••••••••' }}</div>
            </div>
            <div class="d-flex gap-1">
              <button class="btn btn-sm btn-outline-secondary" @click="showPwd = !showPwd">
                <i :class="showPwd ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'" />
              </button>
              <button class="btn btn-sm btn-outline-secondary" @click="copy(wifi.password, $t('Password'))">
                <i class="fa-solid fa-copy" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </details>

    <!-- Loyalty -->
    <details class="card border-0 shadow-sm rounded-3 mb-3">
      <summary class="card-body p-3 d-flex align-items-center" style="cursor: pointer;">
        <div class="bg-warning bg-opacity-25 text-warning rounded-circle d-flex align-items-center justify-content-center me-3"
             style="width: 40px; height: 40px;">
          <i class="fa-solid fa-star" />
        </div>
        <div class="flex-grow-1">
          <div class="fw-bold">{{ $t('Loyalty') }}</div>
          <div class="text-muted small">
            <span v-if="loyalty.member">
              {{ loyalty.member.points }} {{ $t('points') }}
            </span>
            <span v-else>{{ $t('Link your account to earn points') }}</span>
          </div>
        </div>
        <i class="fa-solid fa-chevron-down text-muted" />
      </summary>
      <div class="px-3 pb-3">
        <!-- Not linked: phone input -->
        <div v-if="!loyalty.member" class="bg-light rounded-3 p-3">
          <p class="small text-muted mb-2">
            {{ $t('Enter your phone number to link a loyalty account') }}
          </p>
          <div class="input-group">
            <span class="input-group-text"><i class="fa-solid fa-phone" /></span>
            <input
              v-model="phone"
              type="tel"
              class="form-control"
              :placeholder="$t('e.g. 081-234-5678')"
              maxlength="15"
            >
            <button class="btn btn-theme text-white fw-bold" :disabled="!phone.trim()" @click="linkLoyalty">
              {{ $t('Link') }}
            </button>
          </div>
          <p class="small text-muted mt-2 mb-0">
            <i class="fa-solid fa-circle-info me-1" />
            {{ $t('Earn') }} {{ loyalty.points_per_baht }} {{ $t('point per ฿1') }} · {{ loyalty.redeem_rate }}
          </p>
        </div>

        <!-- Linked: rewards -->
        <div v-else>
          <div class="bg-warning bg-opacity-10 rounded-3 p-3 mb-3 text-center">
            <div class="small text-muted">{{ $t('Available points') }}</div>
            <div class="fs-3 fw-bold text-warning">{{ loyalty.member.points }}</div>
            <div class="small text-muted">{{ $t('Linked to') }} {{ loyalty.member.phone }}</div>
          </div>

          <h6 class="fw-bold small mb-2">{{ $t('Redeem rewards') }}</h6>
          <div
            v-for="r in loyalty.available_rewards"
            :key="r.id"
            class="d-flex justify-content-between align-items-center border rounded-3 p-2 mb-2"
            :class="{ 'opacity-50': r.cost > loyalty.member.points }"
          >
            <div>
              <div class="fw-bold small">{{ $t(r.name) }}</div>
              <div class="small text-muted">{{ r.cost }} {{ $t('points') }}</div>
            </div>
            <button
              class="btn btn-sm btn-outline-warning fw-bold"
              :disabled="r.cost > loyalty.member.points"
              @click="redeem(r)"
            >
              <i class="fa-solid fa-gift me-1" />{{ $t('Redeem') }}
            </button>
          </div>
        </div>
      </div>
    </details>

    <!-- Pre-order -->
    <details class="card border-0 shadow-sm rounded-3 mb-3">
      <summary class="card-body p-3 d-flex align-items-center" style="cursor: pointer;">
        <div class="bg-success bg-opacity-10 text-success rounded-circle d-flex align-items-center justify-content-center me-3"
             style="width: 40px; height: 40px;">
          <i class="fa-solid fa-clock" />
        </div>
        <div class="flex-grow-1">
          <div class="fw-bold">{{ $t('Pre-order') }}</div>
          <div class="text-muted small">{{ $t('Schedule items for later in your meal') }}</div>
        </div>
        <i class="fa-solid fa-chevron-down text-muted" />
      </summary>
      <div class="px-3 pb-3">
        <p class="small text-muted">
          {{ $t('Pick when you want the next round to arrive. Staff will send it to the kitchen at that time.') }}
        </p>

        <div class="row g-2 mb-3">
          <div
            v-for="slot in slots"
            :key="slot.id"
            class="col-6"
          >
            <button
              class="btn w-100 py-2 rounded-3 border fw-bold"
              :class="selectedSlot === slot.id ? 'btn-theme text-white' : 'btn-light'"
              @click="selectedSlot = slot.id"
            >
              <i class="fa-solid fa-clock me-1" />{{ $t(slot.label) }}
            </button>
          </div>
        </div>

        <button
          class="btn btn-theme text-white w-100 fw-bold rounded-3 py-2"
          :disabled="!selectedSlot"
          @click="schedulePreOrder"
        >
          <i class="fa-solid fa-calendar-check me-2" />{{ $t('Confirm pre-order') }}
        </button>

        <div v-if="preOrders.length" class="mt-3">
          <h6 class="fw-bold small mb-2 text-muted text-uppercase">{{ $t('Scheduled') }}</h6>
          <div
            v-for="p in preOrders"
            :key="p.id"
            class="d-flex justify-content-between align-items-center border rounded-3 p-2 mb-2"
          >
            <div>
              <div class="fw-bold small">
                <i class="fa-solid fa-clock me-1 text-theme" />{{ $t(p.slotLabel) }}
              </div>
              <div class="small text-muted">{{ $t('Confirmed at') }} {{ p.confirmedAt }}</div>
            </div>
            <button class="btn btn-sm btn-outline-danger" @click="cancelPreOrder(p.id)">
              <i class="fa-solid fa-xmark" />
            </button>
          </div>
        </div>
      </div>
    </details>

    <!-- Language switcher (also in header — kept here for discoverability) -->
    <details class="card border-0 shadow-sm rounded-3 mb-3">
      <summary class="card-body p-3 d-flex align-items-center" style="cursor: pointer;">
        <div class="bg-secondary bg-opacity-10 text-secondary rounded-circle d-flex align-items-center justify-content-center me-3"
             style="width: 40px; height: 40px;">
          <i class="fa-solid fa-globe" />
        </div>
        <div class="flex-grow-1">
          <div class="fw-bold">{{ $t('Language') }}</div>
          <div class="text-muted small">
            {{ $i18n.locale === 'th' ? 'ภาษาไทย' : 'English' }}
          </div>
        </div>
        <i class="fa-solid fa-chevron-down text-muted" />
      </summary>
      <div class="px-3 pb-3">
        <div class="d-flex gap-2">
          <button
            class="btn flex-fill py-2 rounded-3 border fw-bold"
            :class="$i18n.locale === 'th' ? 'btn-theme text-white' : 'btn-light'"
            @click="switchLang('th')"
          >
            🇹🇭 ภาษาไทย
          </button>
          <button
            class="btn flex-fill py-2 rounded-3 border fw-bold"
            :class="$i18n.locale === 'en' ? 'btn-theme text-white' : 'btn-light'"
            @click="switchLang('en')"
          >
            🇬🇧 English
          </button>
        </div>
      </div>
    </details>

    <p class="text-center text-muted small mt-4">
      <i class="fa-solid fa-utensils me-1" />
      {{ $t('Powered by Siva') }}
    </p>
  </div>
</template>

<script>
import { MOCK_WIFI, MOCK_LOYALTY, MOCK_PREORDER_SLOTS } from './mock-data';
import { setLocale } from '@/plugins/i18n';

export default {
  name: 'TabMore',
  emits: ['notify'],
  data() {
    return {
      wifi: MOCK_WIFI,
      showPwd: false,
      // Clone loyalty so linking mutates local state, not the shared mock.
      loyalty: JSON.parse(JSON.stringify(MOCK_LOYALTY)),
      phone: '',
      slots: MOCK_PREORDER_SLOTS,
      selectedSlot: null,
      preOrders: [],
    };
  },
  methods: {
    copy(text, label) {
      navigator.clipboard?.writeText(text);
      this.$emit('notify', {
        message: label + ' ' + this.$t('copied'),
        variant: 'success',
        icon: 'fa-solid fa-copy',
      });
    },
    linkLoyalty() {
      const phone = this.phone.trim();
      if (!phone) return;
      // Mock: any phone "works" and starts with 250 points.
      this.loyalty.member = { phone, points: 250 };
      this.phone = '';
      this.$emit('notify', {
        message: this.$t('Loyalty account linked'),
        variant: 'success',
        icon: 'fa-solid fa-star',
      });
    },
    redeem(reward) {
      if (!this.loyalty.member || reward.cost > this.loyalty.member.points) return;
      this.loyalty.member.points -= reward.cost;
      this.$emit('notify', {
        message: this.$t('Redeemed') + ': ' + this.$t(reward.name),
        variant: 'success',
        icon: 'fa-solid fa-gift',
      });
    },
    schedulePreOrder() {
      const slot = this.slots.find(s => s.id === this.selectedSlot);
      if (!slot) return;
      this.preOrders.push({
        id: Date.now(),
        slotLabel: slot.label,
        confirmedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      });
      this.selectedSlot = null;
      this.$emit('notify', {
        message: this.$t('Pre-order scheduled') + ': ' + this.$t(slot.label),
        variant: 'success',
        icon: 'fa-solid fa-calendar-check',
      });
    },
    cancelPreOrder(id) {
      this.preOrders = this.preOrders.filter(p => p.id !== id);
    },
    switchLang(lang) {
      setLocale(lang);
    },
  },
};
</script>

<style scoped>
/* Hide native disclosure marker — chevron in template signals state instead. */
details > summary {
  list-style: none;
}
details > summary::-webkit-details-marker {
  display: none;
}
details[open] > summary > .fa-chevron-down {
  transform: rotate(180deg);
}
</style>
